import {
  adminInput,
  adminButtonPrimary,
  adminSectionTitle,
  adminModalPanel,
} from '../../layout/adminStyles';
import React, { FC, FormEvent } from 'react';
import { StoreForm } from './storeForm';

type Props = {
  isShowModal: boolean;
  form: StoreForm;
  isSaving: boolean;
  onChange: (form: StoreForm) => void;
  onClose: () => void;
  onSubmit: () => void;
};

const StoreEditModal: FC<Props> = ({
  isShowModal,
  form,
  isSaving,
  onChange,
  onClose,
  onSubmit,
}) => {
  if (!isShowModal) {
    return null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="store-edit-modal-title"
    >
      <div className={`${adminModalPanel} max-w-2xl`}>
        <div className="mb-4 flex items-center justify-between border-b border-stone-200 pb-3">
          <h2 id="store-edit-modal-title" className={adminSectionTitle + ' mb-0 border-0 pb-0'}>
            店舗情報を編集
          </h2>
          <button
            type="button"
            className="text-2xl leading-none text-stone-500 hover:text-stone-800"
            onClick={onClose}
            aria-label="閉じる"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="store-name" className="mb-1 block text-sm font-medium text-stone-700">
              店舗名
            </label>
            <input
              id="store-name"
              type="text"
              required
              className={`${adminInput} w-full`}
              value={form.name}
              onChange={(e) => onChange({ ...form, name: e.target.value })}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="store-description" className="mb-1 block text-sm font-medium text-stone-700">
              説明（任意）
            </label>
            <textarea
              id="store-description"
              rows={3}
              className={`${adminInput} w-full resize-y`}
              value={form.description}
              onChange={(e) => onChange({ ...form, description: e.target.value })}
            />
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="store-zip" className="mb-1 block text-sm font-medium text-stone-700">
                郵便番号
              </label>
              <input
                id="store-zip"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.zip}
                onChange={(e) => onChange({ ...form, zip: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="store-pref" className="mb-1 block text-sm font-medium text-stone-700">
                都道府県
              </label>
              <input
                id="store-pref"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.pref}
                onChange={(e) => onChange({ ...form, pref: e.target.value })}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="store-city" className="mb-1 block text-sm font-medium text-stone-700">
                市区町村
              </label>
              <input
                id="store-city"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.city}
                onChange={(e) => onChange({ ...form, city: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="store-address" className="mb-1 block text-sm font-medium text-stone-700">
                住所
              </label>
              <input
                id="store-address"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.address}
                onChange={(e) => onChange({ ...form, address: e.target.value })}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="store-building" className="mb-1 block text-sm font-medium text-stone-700">
              建物名（任意）
            </label>
            <input
              id="store-building"
              type="text"
              className={`${adminInput} w-full`}
              value={form.building}
              onChange={(e) => onChange({ ...form, building: e.target.value })}
            />
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="store-latitude" className="mb-1 block text-sm font-medium text-stone-700">
                緯度
              </label>
              <input
                id="store-latitude"
                type="number"
                step="any"
                required
                className={`${adminInput} w-full`}
                value={form.latitude}
                onChange={(e) => onChange({ ...form, latitude: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="store-longitude" className="mb-1 block text-sm font-medium text-stone-700">
                経度
              </label>
              <input
                id="store-longitude"
                type="number"
                step="any"
                required
                className={`${adminInput} w-full`}
                value={form.longitude}
                onChange={(e) => onChange({ ...form, longitude: e.target.value })}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="store-phone" className="mb-1 block text-sm font-medium text-stone-700">
              電話番号
            </label>
            <input
              id="store-phone"
              type="tel"
              required
              className={`${adminInput} w-full`}
              value={form.phone_number}
              onChange={(e) => onChange({ ...form, phone_number: e.target.value })}
            />
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="store-email" className="mb-1 block text-sm font-medium text-stone-700">
                メールアドレス
              </label>
              <input
                id="store-email"
                type="email"
                required
                className={`${adminInput} w-full`}
                value={form.email}
                onChange={(e) => onChange({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="store-login-id" className="mb-1 block text-sm font-medium text-stone-700">
                ログインID
              </label>
              <input
                id="store-login-id"
                type="text"
                required
                className={`${adminInput} w-full`}
                value={form.login_id}
                onChange={(e) => onChange({ ...form, login_id: e.target.value })}
              />
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="store-open-time" className="mb-1 block text-sm font-medium text-stone-700">
                開店時間
              </label>
              <input
                id="store-open-time"
                type="time"
                required
                className={`${adminInput} w-full`}
                value={form.open_time}
                onChange={(e) => onChange({ ...form, open_time: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="store-close-time" className="mb-1 block text-sm font-medium text-stone-700">
                閉店時間
              </label>
              <input
                id="store-close-time"
                type="time"
                required
                className={`${adminInput} w-full`}
                value={form.close_time}
                onChange={(e) => onChange({ ...form, close_time: e.target.value })}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.has_parking}
                onChange={(e) => onChange({ ...form, has_parking: e.target.checked })}
                className="h-4 w-4 rounded border-stone-300 text-teal-500 focus:ring-teal-400"
              />
              <span className="text-sm font-medium text-stone-700">駐車場あり</span>
            </label>
          </div>

          <div className="mb-6">
            <label htmlFor="store-weather-code" className="mb-1 block text-sm font-medium text-stone-700">
              天気コード（任意）
            </label>
            <input
              id="store-weather-code"
              type="text"
              maxLength={6}
              className={`${adminInput} w-full`}
              value={form.weather_code}
              onChange={(e) => onChange({ ...form, weather_code: e.target.value })}
            />
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-xl border border-stone-300 px-5 py-2 text-stone-700 hover:bg-stone-50"
              onClick={onClose}
              disabled={isSaving}
            >
              キャンセル
            </button>
            <button type="submit" className={adminButtonPrimary} disabled={isSaving}>
              {isSaving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoreEditModal;
